import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Activity, DollarSign, Cpu } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    adminApi.metrics().then(r => setMetrics(r.data)).catch(() => {});
    adminApi.billing().then(r => setBilling(r.data)).catch(() => {});
  }, []);

  const usageChartData = metrics?.usage_24h?.map(u => ({
    service: u.service,
    requests: u.requests,
    latency: u.avg_latency,
  })) || [];

  const tooltipStyle = { contentStyle: { background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' } };

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Admin Dashboard</h2>
        <p className="text-muted-foreground mt-1">Platform overview and metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Users</p>
                <p data-testid="admin-total-users" className="text-3xl font-bold mt-1">{metrics?.users?.total || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">+{metrics?.users?.new_7d || 0} this week</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Users</p>
                <p className="text-3xl font-bold mt-1">{metrics?.users?.active || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                <Activity className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Requests (24h)</p>
                <p data-testid="admin-requests-24h" className="text-3xl font-bold mt-1">{metrics?.total_requests_24h || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Revenue (30d)</p>
                <p data-testid="admin-revenue" className="text-3xl font-bold mt-1">${metrics?.revenue_30d?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {usageChartData.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Service Usage (24h)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usageChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="service" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="requests" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {billing?.transactions?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Recent Transactions</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {billing.transactions.slice(0, 10).map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/20">
                  <div>
                    <p className="text-sm font-medium">{t.type === 'wallet_topup' ? 'Wallet Top-up' : 'Payment'}</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">${t.amount?.toFixed(2)}</span>
                    <Badge className={t.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                      {t.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
